import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SiteHeader } from "@/components/site-header";
import { Building2, ArrowLeft, Clock, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Link } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from "react";
import { apiRequest } from "@/lib/queryClient";

interface Affiliation {
  id: string;
  personId: string;
  companyId: string;
  affiliationRole: string;
  affiliationStatus: string;
  createdAt: string;
  company: {
    id: string;
    name: string;
    slug: string;
    logo: string | null;
  };
}

interface Company {
  id: string;
  name: string;
  slug: string;
}

export default function AffiliationsPage() {
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedRole, setSelectedRole] = useState("member");

  const { data: affiliations, isLoading: affiliationsLoading } = useQuery<Affiliation[]>({
    queryKey: ["/api/affiliations/my"],
    enabled: isAuthenticated,
  });

  const { data: companies } = useQuery<Company[]>({
    queryKey: ["/api/companies"],
    enabled: isAuthenticated,
  });

  const requestMutation = useMutation({
    mutationFn: async ({ companyId, role }: { companyId: string; role: string }) => {
      const response = await apiRequest("POST", "/api/affiliations/request", { companyId, role });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/affiliations/my"] });
      toast({
        title: "Request Sent",
        description: "Your affiliation request has been submitted for review.",
      });
      setRequestDialogOpen(false);
      setSelectedCompanyId("");
      setSelectedRole("member");
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Request Failed",
        description: error.message || "Unable to submit affiliation request",
      });
    },
  });

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="container mx-auto max-w-4xl py-8 px-4">
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    setLocation("/login");
    return null;
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return <Badge variant="default" className="bg-green-500">Approved</Badge>;
      case "rejected":
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  const affiliatedCompanyIds = affiliations?.map(a => a.companyId) || [];
  const availableCompanies = companies?.filter(c => !affiliatedCompanyIds.includes(c.id)) || [];

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="container mx-auto max-w-4xl py-8 px-4">
        <div className="mb-6">
          <Link href="/account">
            <Button variant="ghost" size="sm" className="mb-4" data-testid="button-back">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Account
            </Button>
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Company Affiliations</h1>
              <p className="text-muted-foreground">Manage your connections with companies</p>
            </div>
            <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="button-request-affiliation">
                  <Building2 className="mr-2 h-4 w-4" />
                  Request Affiliation
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request Company Affiliation</DialogTitle>
                  <DialogDescription>
                    Select a company to request an affiliation with. An admin will review your request.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Company</label>
                    <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                      <SelectTrigger data-testid="select-company">
                        <SelectValue placeholder="Select a company" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCompanies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Role</label>
                    <Select value={selectedRole} onValueChange={setSelectedRole}>
                      <SelectTrigger data-testid="select-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="contributor">Contributor</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    className="w-full"
                    disabled={!selectedCompanyId || requestMutation.isPending}
                    onClick={() => requestMutation.mutate({ companyId: selectedCompanyId, role: selectedRole })}
                    data-testid="button-submit-request"
                  >
                    {requestMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Submit Request
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {affiliationsLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : affiliations && affiliations.length > 0 ? (
          <div className="space-y-4">
            {affiliations.map((affiliation) => (
              <Card key={affiliation.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{affiliation.company?.name || "Unknown Company"}</CardTitle>
                        <CardDescription className="flex items-center gap-2">
                          Role: <Badge variant="outline">{affiliation.affiliationRole}</Badge>
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(affiliation.affiliationStatus)}
                      {getStatusBadge(affiliation.affiliationStatus)}
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Affiliations Yet</h3>
              <p className="text-muted-foreground text-center mb-4">
                You haven't connected with any companies yet. Request an affiliation to get started.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
